import { AloftPrice } from '@aloft/toolkit-shared';
import { Selector, createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../../types.ts';
import { selectImprovementEntityIds } from '../propertyDataSelectors/improvementsSelectors.ts';
import { createPriceSelector } from './createPriceSelector.ts';

type CostOutput = {
  totalCost: AloftPrice;
  multiplierBreakdown: { zipOnly: AloftPrice; [key: string]: AloftPrice };
};

// Intentionally loose — concrete calculate functions are typed at their own definition site.
// The factory only needs to call calculate internally with unknown args.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CalculateFn<TOutput> = (...args: any[]) => TOutput | undefined;

interface FeatureCostSelectorsNames {
  cost: string;
  zipOnly?: string;
  costs?: string;
  display?: string;
  costOutput?: string;
  subjectOutput?: string;
  subjectArvOutput?: string;
}

type PriceSelector = (state: unknown, id: string) => AloftPrice | undefined;
type PriceSelectorMap = (state: unknown) => Record<string, AloftPrice>;
type DisplaySelector = (state: unknown, id: string) => string | undefined;
type OutputSelector<TOutput> = (state: unknown, id: string) => TOutput | undefined;
type SubjectOutputSelector<TOutput> = (state: unknown) => TOutput | undefined;

type FeatureCostSelectors<TOutput, TNames extends FeatureCostSelectorsNames> = {
  [K in TNames['cost']]: PriceSelector;
} & (TNames['zipOnly'] extends string ? { [K in TNames['zipOnly']]: PriceSelector } : object) &
  (TNames['costs'] extends string ? { [K in TNames['costs']]: PriceSelectorMap } : object) &
  (TNames['display'] extends string ? { [K in TNames['display']]: DisplaySelector } : object) &
  (TNames['costOutput'] extends string
    ? { [K in TNames['costOutput']]: OutputSelector<TOutput> }
    : object) &
  (TNames['subjectOutput'] extends string
    ? { [K in TNames['subjectOutput']]: SubjectOutputSelector<TOutput> }
    : object) &
  (TNames['subjectArvOutput'] extends string
    ? { [K in TNames['subjectArvOutput']]: SubjectOutputSelector<TOutput> }
    : object);

export const createFeatureCostSelectors = <
  TOutput extends CostOutput,
  TCost extends string,
  TZipOnly extends string | undefined = undefined,
  TCosts extends string | undefined = undefined,
  TDisplay extends string | undefined = undefined,
  TCostOutput extends string | undefined = undefined,
  TSubjectOutput extends string | undefined = undefined,
  TSubjectArvOutput extends string | undefined = undefined,
>(config: {
  calculate: CalculateFn<TOutput>;
  inputSelectors: readonly Selector[];
  names: {
    cost: TCost;
    zipOnly?: TZipOnly;
    costs?: TCosts;
    display?: TDisplay;
    costOutput?: TCostOutput;
    subjectOutput?: TSubjectOutput;
    subjectArvOutput?: TSubjectArvOutput;
  };
}): FeatureCostSelectors<
  TOutput,
  {
    cost: TCost;
    zipOnly: TZipOnly;
    costs: TCosts;
    display: TDisplay;
    costOutput: TCostOutput;
    subjectOutput: TSubjectOutput;
    subjectArvOutput: TSubjectArvOutput;
  }
> => {
  const { calculate, inputSelectors, names } = config;

  const idSelector = (_state: RootState, id: string) => id;
  const allInputs = [...inputSelectors, idSelector] as readonly Selector[];

  const getTotalOrBreakdown = (inputs: unknown[], id: string, type: 'totalCost' | 'zipOnly') => {
    const costOutput = calculate(...inputs, id);
    if (!costOutput) return AloftPrice.zero;
    return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
  };

  const getTotal = (inputs: unknown[], id: string) => getTotalOrBreakdown(inputs, id, 'totalCost');

  const result: Record<string, unknown> = {};

  // select<X>Cost
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result[names.cost] = createPriceSelector(allInputs as any, (...args: unknown[]) => {
    const id = args[args.length - 1] as string;
    const inputs = args.slice(0, -1);
    return getTotal(inputs, id);
  });

  // selectZipOnly<X>Cost
  if (names.zipOnly) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result[names.zipOnly] = createPriceSelector(allInputs as any, (...args: unknown[]) => {
      const id = args[args.length - 1] as string;
      const inputs = args.slice(0, -1);
      return getTotalOrBreakdown(inputs, id, 'zipOnly');
    });
  }

  // select<X>Costs
  if (names.costs) {
    result[names.costs] = createSelector(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [...inputSelectors, selectImprovementEntityIds] as any,
      (...args: unknown[]) => {
        const ids = args[args.length - 1] as string[];
        const inputs = args.slice(0, -1);
        return ids.reduce((acc: Record<string, AloftPrice>, propID) => {
          const cost = getTotal(inputs, propID);
          if (cost) acc[propID] = cost;
          return acc;
        }, {});
      },
    );
  }

  // select<X>CostDisplay
  if (names.display) {
    const costSelector = result[names.cost] as Selector;
    result[names.display] = createSelector([costSelector], (cost?: AloftPrice) =>
      cost?.toString(true),
    );
  }

  // select<X>CostOutput
  if (names.costOutput) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result[names.costOutput] = createSelector(allInputs as any, (...args: unknown[]) => {
      const id = args[args.length - 1] as string;
      const inputs = args.slice(0, -1);
      return calculate(...inputs, id);
    });
  }

  // selectSubject<X>CostOutput
  if (names.subjectOutput) {
    result[names.subjectOutput] = createSelector(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inputSelectors as any,
      (...inputs: unknown[]) => calculate(...inputs, 'subject'),
    );
  }

  // selectSubjectArv<X>CostOutput
  if (names.subjectArvOutput) {
    result[names.subjectArvOutput] = createSelector(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inputSelectors as any,
      (...inputs: unknown[]) => calculate(...inputs, 'subjectArv'),
    );
  }

  return result as FeatureCostSelectors<
    TOutput,
    {
      cost: TCost;
      zipOnly: TZipOnly;
      costs: TCosts;
      display: TDisplay;
      costOutput: TCostOutput;
      subjectOutput: TSubjectOutput;
      subjectArvOutput: TSubjectArvOutput;
    }
  >;
};
